import * as React from "react";
import {FC, useState} from "react";
import {StyledDiv, StyledTypography} from "../../../providers/layout/LayoutProvider";
import {PrivateRenderer} from "../PrivateRenderer";
import {useRecoilState, useRecoilValue} from "recoil";
import {LocalizationsAtom} from "../../../providers/localization/LocalizationProvider";
import {ReturnsPagePath} from "../returns/ReturnsPage";
import {Box, Card, CardActionArea} from "@mui/material";
import {ReturnsCartAtom, ReturnsSumNoPayout, ReturnsSumPayout} from "../returns/ReturnsCart";
import {Currency} from "../../../components/Currency";
import {EnumTradeInPayoutType} from "../../../Queries";

export const PayoutPagePath = () => {
   return ReturnsPagePath() + '/payout';
}

export const PayoutPage: FC<any> = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const [returnsCart, setReturnsCart] = useRecoilState(ReturnsCartAtom);
   const [selected, setSelected] = useState<EnumTradeInPayoutType>(returnsCart.payoutType);
   const sumNoPayout = useRecoilValue(ReturnsSumNoPayout);
   const sumPayout = useRecoilValue(ReturnsSumPayout);

   const onChange = (type: EnumTradeInPayoutType) => () => {
      setSelected(type);
      setReturnsCart((cart) => {
         return {
            ...cart,
            payoutType: type
         }
      })
   }

   return <PrivateRenderer step={"payout"}>
      <StyledDiv type={"main"}>
         <Box sx={{
            textAlign: 'center',
            marginBottom: '40px'
         }}>
            <StyledTypography variant={"h1"}>
               {localizations.payoutTitle}
            </StyledTypography>
            <StyledTypography variant={"body1"} style={{marginTop: '10px'}}>
               {localizations.payoutDescription}
            </StyledTypography>
         </Box>

         <Box sx={{
            display: 'flex',
            gap: '20px',
            justifyContent: 'center',
            flexWrap: 'wrap'
         }}>
            <PaymentCard
               title={localizations.payoutVoucher}
               selected={selected === EnumTradeInPayoutType.Voucher}
               onClick={onChange(EnumTradeInPayoutType.Voucher)}
               icon={<svg viewBox="0 0 24 24" width="48" fill="none" stroke="currentColor">
                  <rect x="3" y="6" width="18" height="12" rx="2" strokeWidth="2"/>
                  <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2"/>
               </svg>}
            >
               <Currency sx={{marginLeft: '8px', fontWeight: 'bold'}} amount={sumNoPayout}/>
            </PaymentCard>

            <PaymentCard
               title={localizations.payoutBank}
               selected={selected === EnumTradeInPayoutType.Payout}
               onClick={onChange(EnumTradeInPayoutType.Payout)}
               icon={<svg viewBox="0 0 24 24" width="48" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="8" strokeWidth="2"/>
                  <text x="8" y="16" fontSize="10" fill="currentColor">$</text>
               </svg>}
            >
               <Currency sx={{marginLeft: '8px', fontWeight: 'bold'}} amount={sumPayout}/>
            </PaymentCard>
         </Box>
      </StyledDiv>
   </PrivateRenderer>
}

interface PaymentCardProps {
   title: string;
   children?: React.ReactNode;
   selected: boolean;
   onClick: () => void;
   icon: React.ReactNode;
}

const PaymentCard: FC<PaymentCardProps> = ({title, children, selected, onClick, icon}) => {
   return (
      <Card
         sx={{
            width: 300,
            border: selected ? '2px solid #000' : '2px solid #e0e0e0',
            borderRadius: 1,
            transition: 'all 0.2s ease-in-out'
         }}
      >
         <CardActionArea
            onClick={onClick}
            sx={{
               height: '100%',
               padding: 3,
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               gap: 2
            }}
         >
            <Box sx={{
               color: selected ? '#000' : '#666',
               transition: 'color 0.2s ease-in-out'
            }}>
               {icon}
            </Box>

            <StyledTypography variant="body1" sx={{textAlign: 'center'}}>
               {title}
            </StyledTypography>

            {children}
         </CardActionArea>
      </Card>
   );
};

export default PayoutPage;